/**
 * AudioLoader - Audio File Loading and Decoding
 * 
 * Handles loading audio files from File objects or URLs.
 * Manages URL resolution, encoding, and audio buffer decoding.
 */

import type { ErrorHandler } from '../../utils/errorHandling';
import { ErrorUtils, globalErrorHandler } from '../../utils/errorHandling';
import { BaseDisposable } from '../../utils/Disposable';
import type { AudioContextManager } from './AudioContextManager';

/**
 * Manages audio file loading and decoding.
 */
export class AudioLoader extends BaseDisposable {
  private contextManager: AudioContextManager;
  private errorHandler?: ErrorHandler;
  
  constructor(contextManager: AudioContextManager, errorHandler?: ErrorHandler) {
    super();
    this.contextManager = contextManager;
    this.errorHandler = errorHandler;
  }
  
  /**
   * Load audio file for a node (from File object or URL string)
   * @param options.reportLoadFailure - If false, do not report load failures to the user (e.g. preset filePath missing).
   */
  async loadAudioFile(nodeId: string, file: File | string, options?: { reportLoadFailure?: boolean }): Promise<AudioBuffer> {
    this.ensureNotDestroyed();

    const reportFailure = options?.reportLoadFailure !== false;

    try {
      await this.contextManager.initialize();
      
      const audioContext = this.contextManager.getContext();
      
      let arrayBuffer: ArrayBuffer;
      
      if (file instanceof File) {
        // Read file as ArrayBuffer
        arrayBuffer = await file.arrayBuffer();
        
        // Validate File buffer immediately
        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          console.error(`[AudioLoader] File is empty or invalid:`, {
            fileName: file.name,
            fileSize: file.size,
            arrayBufferSize: arrayBuffer?.byteLength ?? 'null/undefined'
          });
          throw new Error(`Audio file is empty or invalid: ${file.name} (size: ${file.size} bytes)`);
        }
      } else {
        // Load from URL
        arrayBuffer = await this.fetchAudioFromUrl(file);
      }
      
      // Validate array buffer before decoding
      this.validateArrayBuffer(arrayBuffer, file instanceof File ? file.name : file);
      
      // Decode audio data
      const audioBuffer = await this.decodeAudioData(audioContext, arrayBuffer, nodeId, file instanceof File ? file.name : file, reportFailure);
      
      return audioBuffer;
    } catch (error: unknown) {
      // Report error via error handler unless suppressed (e.g. preset filePath load)
      if (reportFailure) {
        const handler = this.errorHandler || globalErrorHandler;
        const errorObj = error instanceof Error ? error : new Error(String(error));
        handler.reportError(
          ErrorUtils.audioError(
            `Could not load this audio file`,
            { originalError: errorObj, nodeId, fileName: file instanceof File ? file.name : file }
          )
        );
      }
      // Re-throw so caller can handle it
      throw error;
    }
  }
  
  /**
   * Fetch audio file from URL with proper encoding and fallback handling.
   */
  private async fetchAudioFromUrl(url: string): Promise<ArrayBuffer> {
    // Encode the URL properly (handle spaces and special characters)
    let urlToFetch = url;
    
    // If it's a relative path (doesn't start with / or http), add a leading /
    if (!url.startsWith('/') && !url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('//')) {
      urlToFetch = '/' + url;
    }
    
    // If it's a relative path starting with /, we need to handle the base path
    if (urlToFetch.startsWith('/') && !urlToFetch.startsWith('//')) {
      // Get base path from import.meta.env.BASE_URL (Vite provides this)
      let baseUrl: string | undefined;
      try {
        baseUrl = import.meta.env?.BASE_URL;
      } catch (e) {
        // Ignore - will use fallback
      }
      
      // Fallback: check if we're in production by looking at the current path
      if (!baseUrl || baseUrl === '/' || baseUrl === '') {
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/ShaderNoice/')) {
          baseUrl = '/ShaderNoice';
        } else if (currentPath.startsWith('/shadernoice/')) {
          baseUrl = '/shadernoice';
        } else if (currentPath.startsWith('/shader-composer/')) {
          baseUrl = '/shader-composer';
        } else {
          baseUrl = '/';
        }
      }
      
      // Apply base URL if it's not just '/'
      if (baseUrl && baseUrl !== '/' && baseUrl !== '') {
        // Remove trailing slash from baseUrl and add the file path; ensure result starts with /
        const cleanBase = baseUrl.replace(/\/$/, '');
        urlToFetch = (cleanBase.startsWith('/') ? cleanBase : '/' + cleanBase) + urlToFetch;
      }
    }
    
    // Encode URL safely.
    // - For absolute http(s) URLs: use encodeURI so we don't break the scheme/host (":" and "/").
    // - For same-origin paths: encode each path segment so spaces/special chars are handled correctly.
    if (urlToFetch.startsWith('http://') || urlToFetch.startsWith('https://') || urlToFetch.startsWith('//')) {
      urlToFetch = encodeURI(urlToFetch);
    } else {
      const urlParts = urlToFetch.split('/');
      const encodedParts = urlParts.map((part, index) => {
        // Don't encode the first empty part (for absolute paths starting with /)
        if (index === 0 && part === '') return '';
        return encodeURIComponent(part);
      });
      urlToFetch = encodedParts.join('/');
    }
    
    // Use absolute URL for same-origin paths so we always hit the right origin (fixes empty response when base path or relative resolution is wrong)
    const fetchUrl = urlToFetch.startsWith('/') && !urlToFetch.startsWith('//')
      ? window.location.origin + urlToFetch
      : urlToFetch;
    
    // Try to fetch the file with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    let response: Response;
    try {
      response = await fetch(fetchUrl, { 
        signal: controller.signal,
        cache: 'no-cache' // Prevent stale cache issues
      });
    } catch (fetchError: unknown) {
      clearTimeout(timeoutId);
      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        throw new Error(`Timeout loading audio file: ${url} (took longer than 30 seconds)`, { cause: fetchError });
      }
      const msg = fetchError instanceof Error ? fetchError.message : String(fetchError);
      throw new Error(`Network error loading audio file: ${url} - ${msg}`, { cause: fetchError });
    }
    clearTimeout(timeoutId);
    
    // If response is not OK, or if response is OK but body is empty, try fallback URLs
    const shouldTryFallback = !response.ok || (response.ok && response.headers.get('content-length') === '0');
    
    if (shouldTryFallback && urlToFetch !== url && url.startsWith('/')) {
      // Try fallback: Original file path without base URL (for Vite dev server)
      const fallbackParts = url.split('/');
      const fallbackEncoded = fallbackParts.map((part, index) => {
        if (index === 0 && part === '') return '';
        return encodeURIComponent(part);
      });
      const fallbackUrl = fallbackEncoded.join('/');
      const fallbackFetchUrl = fallbackUrl.startsWith('/') && !fallbackUrl.startsWith('//')
        ? window.location.origin + fallbackUrl
        : fallbackUrl;
      
      const fallbackController = new AbortController();
      const fallbackTimeoutId = setTimeout(() => fallbackController.abort(), 30000);
      try {
        const fallbackResponse = await fetch(fallbackFetchUrl, { 
          signal: fallbackController.signal,
          cache: 'no-cache'
        });
        clearTimeout(fallbackTimeoutId);
        
        // Check if fallback response is better (OK and has content)
        const fallbackContentLength = fallbackResponse.headers.get('content-length');
        const isFallbackBetter = fallbackResponse.ok && 
          fallbackContentLength !== '0' && 
          (!response.ok || (response.ok && response.headers.get('content-length') === '0'));
        
        if (isFallbackBetter) {
          response = fallbackResponse;
          urlToFetch = fallbackUrl;
        }
      } catch (e) {
        clearTimeout(fallbackTimeoutId);
        // Ignore fallback errors, use original response
        console.warn(`[AudioLoader] Fallback URL failed:`, e);
      }
    }
    
    const contentLength = response.headers.get('content-length');
    const contentType = response.headers.get('content-type');
    
    if (!response.ok) {
      // Try to read error body for more info
      const errorText = await response.text().catch(() => 'Unable to read error response');
      throw new Error(
        `Failed to load audio file from URL: ${url} (tried: ${urlToFetch}, status: ${response.status} ${response.statusText}). ` +
        `Response: ${errorText.substring(0, 200)}. ` +
        `Make sure the file exists and is accessible.`
      );
    }
    
    // Check content length if available - this is an early warning
    if (contentLength && parseInt(contentLength, 10) === 0) {
      console.error(`[AudioLoader] Content-Length header indicates 0 bytes`);
      throw new Error(`Audio file is empty (0 bytes): ${url} (tried: ${urlToFetch}). Make sure the file exists and is not corrupted.`);
    }
    
    // Check content type to ensure it's an audio file
    if (contentType && !contentType.startsWith('audio/') && !contentType.includes('octet-stream')) {
      console.warn(`[AudioLoader] Unexpected content type for audio file: ${contentType} (URL: ${urlToFetch})`);
    }
    
    // CRITICAL: Ensure response body hasn't been consumed
    if (response.bodyUsed) {
      console.error(`[AudioLoader] Response body already consumed! This should not happen.`);
      throw new Error(`Response body was already consumed before reading. This indicates a bug in the code.`);
    }
    
    // Read the array buffer - this consumes the response body
    try {
      if (response.bodyUsed) {
        throw new Error('Response body was already consumed');
      }
      
      const buffer = await response.arrayBuffer();
      
      // Verify we actually got data
      if (!buffer) {
        throw new Error('response.arrayBuffer() returned null/undefined');
      }
      
      return buffer;
    } catch (arrayBufferError: unknown) {
      const err = arrayBufferError instanceof Error ? arrayBufferError : new Error(String(arrayBufferError));
      console.error(`[AudioLoader] Failed to read response.arrayBuffer():`, {
        error: err.message,
        errorStack: err.stack,
        responseStatus: response.status,
        responseOk: response.ok,
        bodyUsed: response.bodyUsed,
        contentType,
        contentLength,
        url: urlToFetch
      });
      
      throw new Error(
        `Failed to read audio file response body: ${err.message}. ` +
        `File: ${url}, URL: ${urlToFetch}, Status: ${response.status}. ` +
        `This might be a CORS issue, network error, or the response body was already consumed.`,
        { cause: arrayBufferError }
      );
    }
  }
  
  /**
   * Validate array buffer before decoding.
   */
  private validateArrayBuffer(arrayBuffer: ArrayBuffer, fileName: string): void {
    if (!arrayBuffer) {
      throw new Error(`Audio file ArrayBuffer is null or undefined: ${fileName}`);
    }
    
    if (arrayBuffer.byteLength === 0) {
      console.error(`[AudioLoader] ArrayBuffer is empty:`, {
        fileName,
        arrayBufferType: typeof arrayBuffer,
        arrayBufferConstructor: arrayBuffer.constructor.name,
        byteLength: arrayBuffer.byteLength
      });
      throw new Error(
        `Audio file is empty (0 bytes): ${fileName}. ` +
        `This usually means the file doesn't exist at that path, the server returned an empty response, ` +
        `or the response body couldn't be read (CORS issue). Check the browser network tab to see what the server actually returned.`
      );
    }
    
    // Verify we didn't get an HTML error page
    if (arrayBuffer.byteLength > 0) {
      const view = new Uint8Array(arrayBuffer, 0, Math.min(100, arrayBuffer.byteLength));
      const textDecoder = new TextDecoder('utf-8', { fatal: false });
      const preview = textDecoder.decode(view);
      if (preview.trim().toLowerCase().startsWith('<!doctype') || preview.trim().toLowerCase().startsWith('<html')) {
        throw new Error(`Server returned HTML instead of audio file. File: ${fileName}. This usually means the file doesn't exist and the server returned a 404 page.`);
      }
    }
  }
  
  /**
   * Decode audio data from array buffer.
   */
  private async decodeAudioData(
    audioContext: AudioContext,
    arrayBuffer: ArrayBuffer,
    nodeId: string,
    fileName: string,
    reportFailure: boolean = true
  ): Promise<AudioBuffer> {
    // Ensure context is in a valid state (not closed)
    if (audioContext.state === 'closed') {
      throw new Error('AudioContext is closed and cannot decode audio');
    }
    
    // Final validation before decoding
    if (arrayBuffer.byteLength === 0) {
      throw new Error(
        `CRITICAL: ArrayBuffer became empty right before decodeAudioData. ` +
        `This indicates a serious bug - the buffer was validated but became empty. ` +
        `File: ${fileName}, Size: ${arrayBuffer.byteLength} bytes`
      );
    }
    
    let audioBuffer: AudioBuffer;
    try {
      audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    } catch (decodeError: unknown) {
      const errorMessage = decodeError instanceof Error ? decodeError.message : String(decodeError);
      const contextState = audioContext.state;
      const bufferSize = arrayBuffer.byteLength;
      
      // Report error via error handler unless suppressed (e.g. preset filePath load)
      const handler = this.errorHandler || globalErrorHandler;
      
      // If buffer is empty, the fetch returned no data (wrong path, 404 as 200, or server issue)
      if (bufferSize === 0) {
        const error = new Error(
          `Audio file empty or not found (0 bytes). ` +
          `File: ${fileName}. ` +
          `Check that the file exists in public/ and the path is correct.`,
          { cause: decodeError }
        );
        if (reportFailure) {
          handler.report(
            'audio',
            'warning',
            `Could not load audio (file missing or empty)`,
            { originalError: error, nodeId, fileName, contextState, bufferSize }
          );
        }
        throw error;
      }
      
      // Provide detailed error information for other decode errors
      const error = new Error(
        `Failed to decode audio data: ${errorMessage}. ` +
        `AudioContext state: ${contextState}, ` +
        `ArrayBuffer size: ${bufferSize} bytes, ` +
        `File: ${fileName}. ` +
        `The file may be corrupted, in an unsupported format, or the AudioContext may be in an invalid state.`,
        { cause: decodeError }
      );
      if (reportFailure) {
        handler.reportError(
          ErrorUtils.audioError(
            `Could not decode this audio file`,
            { originalError: error, nodeId, fileName, contextState, bufferSize }
          )
        );
      }
      throw error;
    }
    
    // Validate decoded audio buffer
    if (!audioBuffer || audioBuffer.length === 0) {
      const error = new Error(`Decoded audio buffer is empty for file: ${fileName}`);
      if (reportFailure) {
        const handler = this.errorHandler || globalErrorHandler;
        handler.reportError(
          ErrorUtils.audioError(
            `This audio file has no usable samples`,
            { originalError: error, nodeId, fileName }
          )
        );
      }
      throw error;
    }
    
    return audioBuffer;
  }
  
  /**
   * Clean up resources.
   */
  protected doDestroy(): void {}
}
